import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Calendar,
  User,
  Phone,
  CreditCard,
  DoorOpen,
  Receipt,
  Search,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";

export default function HotelBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all, checked-in, reserved

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await api.get("/hotel/bookings/active");
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.guest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.room_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.booking_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.guest_phone?.includes(searchQuery);

    const matchesFilter = filter === "all" || booking.status.toLowerCase() === filter;

    return matchesSearch && matchesFilter;
  });

  const handleCheckOut = (bookingId) => {
    // Navigate to check-out page
    window.location.href = `/hotel-checkout/${bookingId}`;
  };

  const handleViewFolio = (bookingId) => {
    // Navigate to check-out page to view bill
    window.location.href = `/hotel-checkout/${bookingId}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-full">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#2D2C2A]">
            Active Bookings
          </h1>
          <p className="text-sm text-[#7A7873] mt-1">
            Manage current check-ins and reservations
          </p>
        </div>
        <Button onClick={fetchBookings} variant="outline" className="gap-2">
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by guest name, room, phone, booking code..."
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
            size="sm"
          >
            All ({bookings.length})
          </Button>
          <Button
            variant={filter === "checked-in" ? "default" : "outline"}
            onClick={() => setFilter("checked-in")}
            size="sm"
          >
            Checked-In ({bookings.filter(b => b.status === "Checked-In").length})
          </Button>
          <Button
            variant={filter === "reserved" ? "default" : "outline"}
            onClick={() => setFilter("reserved")}
            size="sm"
          >
            Reserved ({bookings.filter(b => b.status === "Reserved").length})
          </Button>
        </div>
      </div>

      {/* Bookings List */}
      <div className="grid gap-4">
        {filteredBookings.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-[#7A7873]">No bookings found</p>
          </Card>
        ) : (
          filteredBookings.map((booking) => (
            <Card key={booking.id} className="p-6 hover:shadow-md transition-shadow">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Booking Info */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Room & Status */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <DoorOpen size={20} className="text-[#D4AF37]" />
                      <span className="font-bold text-xl">Room {booking.room_number}</span>
                    </div>
                    <Badge
                      className={
                        booking.status === "Checked-In"
                          ? "bg-green-500"
                          : "bg-blue-500"
                      }
                    >
                      {booking.status}
                    </Badge>
                    <div className="text-xs text-[#7A7873] mt-1">
                      {booking.category_name}
                    </div>
                  </div>

                  {/* Guest Info */}
                  <div>
                    <div className="flex items-center gap-2 text-sm text-[#7A7873] mb-1">
                      <User size={16} />
                      Guest Details
                    </div>
                    <div className="font-semibold">{booking.guest_name}</div>
                    <div className="flex items-center gap-1 text-sm text-[#7A7873]">
                      <Phone size={14} />
                      {booking.guest_phone}
                    </div>
                  </div>

                  {/* Dates */}
                  <div>
                    <div className="flex items-center gap-2 text-sm text-[#7A7873] mb-1">
                      <Calendar size={16} />
                      Stay Period
                    </div>
                    <div className="text-sm">
                      <div><strong>In:</strong> {booking.check_in_date}</div>
                      <div><strong>Out:</strong> {booking.check_out_date}</div>
                      <div className="text-xs text-[#7A7873]">
                        {booking.total_nights} {booking.total_nights === 1 ? 'night' : 'nights'}
                      </div>
                    </div>
                  </div>

                  {/* Booking Details */}
                  <div>
                    <div className="flex items-center gap-2 text-sm text-[#7A7873] mb-1">
                      <CreditCard size={16} />
                      Booking Info
                    </div>
                    <div className="text-xs">
                      <div><strong>Code:</strong> {booking.booking_code}</div>
                      <div><strong>Plan:</strong> {booking.plan_type}</div>
                      <div><strong>Guests:</strong> {booking.adults} Adults, {booking.children} Children</div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex lg:flex-col gap-2">
                  <Button
                    onClick={() => handleViewFolio(booking.id)}
                    variant="outline"
                    className="flex-1 lg:flex-none gap-2"
                  >
                    <Receipt size={16} />
                    View Bill
                  </Button>
                  {booking.status === "Checked-In" && (
                    <Button
                      onClick={() => handleCheckOut(booking.id)}
                      className="flex-1 lg:flex-none gap-2 bg-[#D4AF37] hover:bg-[#C5A028]"
                    >
                      <DoorOpen size={16} />
                      Check-Out
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
